/**
 * User Profile Service — Supabase Direct
 *
 * Holds the inline `supabase.from('users').update(...)` mutations the
 * AuthenticationService profile hooks (`useProfile` / `useRootProfile`) used to
 * run directly. Password changes go through `supabase.auth.updateUser` (an
 * Auth-API call, not a table read) and stay in the hook. Each helper preserves
 * the EXACT update / `.select().single()` shape of the call site it replaced
 * and returns the raw `{ data, error }` result.
 *
 * New path: Supabase client → PostgreSQL (direct, RLS-secured).
 */
import { supabase } from '../supabase';

/** Fields a user can patch from their own profile screen. */
export interface UserProfilePatch {
  first_name?: string;
  last_name?: string;
  /** Public URL from the `avatars` bucket; `null` clears the photo. */
  avatar_url?: string | null;
}

/**
 * Update a `users` row (own profile / root profile) and return the updated
 * row. Returns the raw `{ data, error }` result; the caller throws on error
 * and feeds `data` into `updateProfile(...)`.
 */
export const updateUserProfile = (userId: string, { first_name, last_name, avatar_url }: UserProfilePatch) =>
  supabase
    .from('users')
    .update({ first_name, last_name, avatar_url })
    .eq('id', userId)
    .select()
    .single();
